import { PUZZLE_STATUSES, PUZZLE_TYPES, PuzzleStatusConstants } from 'app-constants';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useFirstRender } from 'hooks';
import React, { useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Splash } from 'shared/Components';
import { LinkButton } from 'shared/Typhography/LinkButton';
import styled from 'styled-components';
import { Puzzle } from 'types';
import { buildPreviousVersionUrl } from 'utilities/puzzleData';

import { firestore, STORAGE_URL } from '../../../../services';
import {
  InformationModal,
  InformationModalTypes,
} from '../../../../shared/Components/InformationModal';
import { backgroundColors } from '../../../../shared/styling';
import { Box, FlexContainer, Image, Text } from '../../../../shared/Typhography';
import { getPuzzlePremiumTierLabel } from '../CreatePuzzleScreen/utilities';

interface ParamTypes {
  id: string;
}

const LabelWrapper = styled.div`
  margin: 5px;
`;

const Badge = styled.div<{ background: string }>`
  padding: 5px;
  background: ${({ background }) => background};
  border-radius: 6px;
  color: white;
  width: max-content;
  margin: 0 4px 0 0;
`;

type PropsParameters = {
  puzzleData?: Puzzle;
  newArtistPortalMode?: boolean;
};

export default function ViewPuzzleScreen(props: PropsParameters) {
  const { id } = useParams<ParamTypes>();
  const { puzzleData: passedPuzzleData, newArtistPortalMode } = props;

  const isFirstRender = useFirstRender();
  const navigate = useHistory();

  const [collections, setCollections] = React.useState<any>(null);
  const [artist, setArtist] = React.useState<any>(null);
  const [insights, setInsights] = React.useState<any>(null);
  const [puzzle, setPuzzle] = React.useState<any>(null);

  const haveCollections = !!puzzle?.collections?.length;
  const previousVersionUrl = puzzle ? buildPreviousVersionUrl(puzzle) : undefined;

  const fetchPuzzle = async () => {
    const docRef = doc(firestore, `puzzles/${id}`);
    const docData = await getDoc(docRef);
    setPuzzle({
      uuid: docData.id,
      ...docData.data(),
    });
  };

  const fetchCollections = async (ids: string[]) => {
    const tmpCollections = [];
    for (let i = 0; i < ids.length; i++) {
      const docRef = doc(firestore, `collections/${ids[i]}`);
      const docData = await getDoc(docRef);
      tmpCollections.push({
        uuid: docData.id,
        ...docData.data(),
      });
    }

    setCollections(tmpCollections);
  };

  const fetchArtist = async (artistId: string) => {
    const docRef = doc(firestore, `usersWeb/${artistId}`);
    const docData = await getDoc(docRef);
    setArtist({
      uuid: docData.id,
      ...docData.data(),
    });
  };

  const fetchInsights = async (artistId: string) => {
    const insightsRef = query(
      collection(firestore, `usersWeb/${artistId}/insights`),
      where('puzzleId', '==', id),
    );

    const docs = await getDocs(insightsRef);
    let totalRevenue = 0;
    let artistRevenue = 0;
    docs.docs.forEach((item) => {
      const inisght = item.data();
      totalRevenue += inisght.revenue;
      artistRevenue += inisght.revenue * (inisght.shares ?? artist?.revenueShare);
    });

    setInsights({
      total: totalRevenue > 0 ? totalRevenue.toFixed(2) : 0,
      artist: artistRevenue > 0 ? artistRevenue.toFixed(2) : 0,
      company: totalRevenue - artistRevenue > 0 ? (totalRevenue - artistRevenue).toFixed(2) : 0,
    });
  };

  React.useEffect(() => {
    if (!isFirstRender || passedPuzzleData) return;

    fetchPuzzle();
  }, [isFirstRender, passedPuzzleData]);

  React.useEffect(() => {
    if (!puzzle?.uuid) return;

    if (!newArtistPortalMode) fetchArtist(puzzle?.Publisher);
    if (haveCollections) fetchCollections(puzzle.collections);
    if (!newArtistPortalMode) fetchInsights(puzzle?.Publisher);
  }, [puzzle, newArtistPortalMode]);

  React.useEffect(() => {
    if (passedPuzzleData) setPuzzle(passedPuzzleData);
  }, [passedPuzzleData]);

  const previewImageUrl = useMemo(
    () => (puzzle?.type === 'video' ? puzzle?.videoIconUrl ?? puzzle?.iconURL : puzzle?.iconURL),
    [puzzle],
  );

  const previewImageUrlLink = useMemo(() => {
    if (puzzle?.type === 'video') {
      return `${STORAGE_URL}/${puzzle?._meta?.original}`;
    }

    return puzzle?.url;
  }, [puzzle]);

  return (
    <>
      {puzzle && (
        <FlexContainer
          flexDirection="row"
          margin={newArtistPortalMode ? '0' : '150px auto 50px'}
          justifyContent="center"
          gap="20px"
          padding="32px"
          flexWrap="wrap"
        >
          <FlexContainer flexDirection="column" alignItems="center" gap="20px" flexWrap="wrap">
            {puzzle.status === PUZZLE_STATUSES.deleted ? (
              <Image
                src={previewImageUrl}
                borderRadiusType="rounded"
                alt="Picture"
                maxWidth="256px"
                maxHeight="256px"
                mobWidth="256px"
                mobHeight="256px"
              />
            ) : (
              <a href={previewImageUrlLink} target="_blank" rel="noreferrer">
                <Image
                  cursor="pointer"
                  src={previewImageUrl}
                  borderRadiusType="rounded"
                  alt="Picture"
                  maxWidth="256px"
                  maxHeight="256px"
                  mobWidth="256px"
                  mobHeight="256px"
                />
              </a>
            )}

            {!newArtistPortalMode && puzzle?.status !== PUZZLE_STATUSES.deleted && (
              <>
                <Button
                  disabled={puzzle.status === PUZZLE_STATUSES.processing}
                  onClick={() =>
                    navigate.push(
                      puzzle.type === PUZZLE_TYPES.video
                        ? `/manage-video-puzzle/${puzzle?.uuid}`
                        : `/edit-puzzle/${puzzle?.uuid}`,
                    )
                  }
                  style={{ width: '100%', height: '32px' }}
                >
                  Edit Puzzle
                </Button>

                {puzzle.status === PUZZLE_STATUSES.edited && (
                  <LinkButton href={previousVersionUrl} target="_blank" rel="noopener noreferrer">
                    Previous Version
                  </LinkButton>
                )}
              </>
            )}
          </FlexContainer>

          <FlexContainer flexDirection="column" flex="1" gap="20px">
            <Box margin="0" width="100%">
              <Text weight="bold" size={1.5}>
                Puzzle Info
              </Text>
              <FlexContainer flexDirection="row" flexWrap="wrap" justifyContent="space-between">
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Name
                  </Text>
                  <Text size={1.5}>{puzzle?.name}</Text>
                </LabelWrapper>
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Status
                  </Text>

                  {/* TODO Add more status colors when self service is implemented */}
                  <div style={{ display: 'flex' }}>
                    <Badge
                      background={
                        (PuzzleStatusConstants as any)?.[puzzle?.status || PUZZLE_STATUSES.approved]
                          ?.color || backgroundColors.inProgress
                      }
                    >
                      {puzzle?.status}
                    </Badge>
                    {(puzzle?.status === PUZZLE_STATUSES.rejected ||
                      puzzle?.status === PUZZLE_STATUSES.edited) && (
                      <InformationModal
                        type={InformationModalTypes.Error}
                        title="Rejection reason"
                        content={puzzle.rejectMessage}
                      />
                    )}
                  </div>
                </LabelWrapper>
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Plays
                  </Text>
                  <Text size={1.5}>{puzzle?.plays}</Text>
                </LabelWrapper>
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Likes
                  </Text>
                  <Text size={1.5}>{puzzle?.likes}</Text>
                </LabelWrapper>
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Keywords
                  </Text>
                  <FlexContainer>
                    {puzzle?.keywords?.split(' ')?.map((keyword: string) => (
                      <Badge background="#585CEA" key={keyword}>
                        {keyword}
                      </Badge>
                    ))}
                  </FlexContainer>
                </LabelWrapper>
              </FlexContainer>
            </Box>
            <Box margin="0" width="100%">
              <Text weight="bold" size={1.5}>
                Puzzle Categories
              </Text>
              <FlexContainer flexDirection="row" flexWrap="wrap" justifyContent="space-between">
                {collections && (
                  <>
                    <LabelWrapper>
                      <Text weight="bold" size={0.9}>
                        Primary Category
                      </Text>
                      <Text size={1.5}>{collections[0]?.name}</Text>
                    </LabelWrapper>

                    <LabelWrapper>
                      <Text weight="bold" size={0.9}>
                        Secondary Category
                      </Text>
                      <Text size={1.5}>{collections[1] ? collections[1]?.name : <>Not Set</>}</Text>
                    </LabelWrapper>
                    <LabelWrapper>
                      <Text weight="bold" size={0.9}>
                        AI Generated
                      </Text>
                      <Text size={1.5}>{puzzle?.aiGenerated ? 'True' : 'False'}</Text>
                    </LabelWrapper>
                    <LabelWrapper>
                      <Text weight="bold" size={0.9}>
                        Price
                      </Text>
                      <Text size={1.5}>{getPuzzlePremiumTierLabel(puzzle?.premiumTier)}</Text>
                    </LabelWrapper>
                    <LabelWrapper>
                      <Text weight="bold" size={0.9}>
                        Puzzle type
                      </Text>
                      <Text size={1.5} style={{ textTransform: 'uppercase' }}>
                        {puzzle?.type ?? 'static'}
                      </Text>
                    </LabelWrapper>
                  </>
                )}
                {haveCollections && !collections && <Splash width={64} />}
              </FlexContainer>
            </Box>
            <Box margin="0" width="100%">
              <Text weight="bold" size={1.5}>
                Puzzle Metadata
              </Text>
              <FlexContainer flexDirection="row" flexWrap="wrap" justifyContent="space-between">
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Image Resolution
                  </Text>
                  <Text size={1.5}>{puzzle?._meta?.resolution}</Text>
                </LabelWrapper>
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Original Resolution
                  </Text>
                  <Text size={1.5}>{puzzle?._meta?.originalResolution}</Text>
                </LabelWrapper>
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Created At
                  </Text>
                  <Text size={1.5}>
                    {puzzle?.createdAt.toDate().toGMTString().replace('GMT', '')}
                  </Text>
                </LabelWrapper>
                <LabelWrapper>
                  <Text weight="bold" size={0.9}>
                    Published At
                  </Text>
                  <Text size={1.5}>
                    {puzzle?.publishedAt
                      ? puzzle?.publishedAt.toDate().toGMTString().replace('GMT', '')
                      : 'Not published'}
                  </Text>
                </LabelWrapper>
              </FlexContainer>
            </Box>
            {!newArtistPortalMode && (
              <>
                <Box margin="0" width="100%">
                  <Text weight="bold" size={1.5}>
                    Artist Info
                  </Text>
                  <FlexContainer flexDirection="row" flexWrap="wrap" justifyContent="space-between">
                    {artist && (
                      <>
                        <LabelWrapper>
                          <Text weight="bold" size={0.9}>
                            Profile Image
                          </Text>
                          <Image
                            src={artist?.profileImageUrl}
                            borderRadiusType="circle"
                            alt="Picture"
                            maxWidth="128px"
                            maxHeight="128px"
                            mobWidth="92px"
                            mobHeight="92px"
                          />
                        </LabelWrapper>
                        <LabelWrapper>
                          <Text weight="bold" size={0.9}>
                            Name
                          </Text>
                          <Text size={1.5}>{artist?.Name ?? artist?.name}</Text>
                        </LabelWrapper>
                        <LabelWrapper>
                          <Text weight="bold" size={0.9}>
                            Revenue Share
                          </Text>
                          <Text size={1.5}>{artist?.revenueShare * 100}%</Text>
                        </LabelWrapper>
                      </>
                    )}
                    {!artist && <Splash width={64} />}
                  </FlexContainer>
                </Box>
                <Box margin="0" width="100%">
                  <Text weight="bold" size={1.5}>
                    Insights
                  </Text>
                  <FlexContainer flexDirection="row" flexWrap="wrap" justifyContent="space-between">
                    {insights && (
                      <>
                        <LabelWrapper>
                          <Text weight="bold" size={0.9}>
                            Revenue
                          </Text>
                          <Text size={1.5}>$ {insights.total}</Text>
                        </LabelWrapper>
                        <LabelWrapper>
                          <Text weight="bold" size={0.9}>
                            Artist Royalties
                          </Text>
                          <Text size={1.5}>$ {insights.artist}</Text>
                        </LabelWrapper>
                      </>
                    )}
                    {!insights && <Splash width={64} />}
                  </FlexContainer>
                </Box>
              </>
            )}
          </FlexContainer>
        </FlexContainer>
      )}

      {!puzzle && <Splash />}
    </>
  );
}
